import type { MusicVideoClipSource } from 'fym-shared/src/music24/musicVideoDoc';

/** The drag type of a footage card (design b1: "Drag a clip onto the timeline, or onto the frame"). */
export const FOOTAGE_DRAG_TYPE = 'application/x-music24-footage';

/** What a dragged card carries: the source, and the length of the footage (null: not known). */
export interface FootageDragData {
  source: MusicVideoClipSource;
  durationSec: number | null;
}

/** Puts a card on the drag. */
export function writeFootageDrag(transfer: DataTransfer, data: FootageDragData): void {
  transfer.setData(FOOTAGE_DRAG_TYPE, JSON.stringify(data));
  transfer.effectAllowed = 'copy';
}

/** True while a footage card is dragged (the data itself is readable only at the drop). */
export function isFootageDrag(transfer: DataTransfer): boolean {
  return Array.from(transfer.types).includes(FOOTAGE_DRAG_TYPE);
}

/** The full drag data at the drop, or null for any other drag. Pure apart from the read. */
export function readFootageDragData(transfer: DataTransfer): FootageDragData | null {
  const raw = transfer.getData(FOOTAGE_DRAG_TYPE);
  if (raw === '') {
    return null;
  }
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== 'object' || value === null) {
      return null;
    }
    const { source, durationSec } = value as { source?: unknown; durationSec?: unknown };
    if (typeof source !== 'object' || source === null) {
      return null;
    }
    const { kind, clipUuid } = source as { kind?: unknown; clipUuid?: unknown };
    if ((kind !== 'bank' && kind !== 'footage') || typeof clipUuid !== 'string' || clipUuid === '') {
      return null;
    }
    return {
      source: { kind, clipUuid },
      durationSec: typeof durationSec === 'number' && Number.isFinite(durationSec) ? durationSec : null,
    };
  } catch {
    return null;
  }
}

/** The source of a dropped card, or null. */
export function readFootageDrag(transfer: DataTransfer): MusicVideoClipSource | null {
  return readFootageDragData(transfer)?.source ?? null;
}
